import { useCallback, useMemo } from 'react';
import { defineMessages, useIntl } from 'react-intl';

import {
  calculateTotalCarbs,
  calculateTotalFat,
  calculateTotalProtein,
} from '../../utils';

// ─────────────────────────────────────────────────────────────────────────────

/**
 * Generates a memoized array of formatted nutrition data based on the provided ingredients.
 *
 * The hook calculates the total carbohydrates, protein, fat, and calories
 * for the given ingredients and formats these values.
 *
 * Each entry in the returned array includes a label (e.g., "Carbohydrates", "Protein") and the
 * corresponding formatted value (e.g., "30G").
 *
 * The hook omits entries for any nutritional metric that is not valid.
 */
export const useProductNutritionData = (params: Params): NutritionData => {
  const { ingredients, calories, useFallbackValues } = params;

  const { formatMessage } = useIntl();

  // ─── Computed Data ───────────────────────────────────────────────────

  const fallbackValue = useFallbackValues ? 0 : null;

  const totalCarbs = calculateTotalCarbs(ingredients) ?? fallbackValue;
  const totalProtein = calculateTotalProtein(ingredients) ?? fallbackValue;
  const totalFat = calculateTotalFat(ingredients) ?? fallbackValue;

  const hasValidCarbs = typeof totalCarbs === 'number';
  const hasValidProtein = typeof totalProtein === 'number';
  const hasValidFat = typeof totalFat === 'number';
  const hasValidCalories = typeof calories === 'number';

  // ─── Messages ────────────────────────────────────────────────────────

  const carbsLabel = formatMessage(messages.carbs);
  const proteinLabel = formatMessage(messages.protein);
  const fatLabel = formatMessage(messages.fat);
  const caloriesLabel = formatMessage(messages.calories);

  // ─── Helpers ─────────────────────────────────────────────────────────

  const formatGramValue = useCallback(
    (value: number) => formatMessage(messages.gramValue, { value }),
    [formatMessage],
  );

  // ─────────────────────────────────────────────────────────────────────

  return useMemo<NutritionData>(() => {
    const carbsEntry = hasValidCarbs
      ? [{ label: carbsLabel, value: formatGramValue(totalCarbs) }]
      : [];

    const proteinEntry = hasValidProtein
      ? [{ label: proteinLabel, value: formatGramValue(totalProtein) }] // prettier-ignore
      : [];

    const fatEntry = hasValidFat
      ? [{ label: fatLabel, value: formatGramValue(totalFat) }]
      : [];

    const caloriesEntry = hasValidCalories
      ? [{ label: caloriesLabel, value: calories }]
      : [];

    return [...proteinEntry, ...carbsEntry, ...fatEntry, ...caloriesEntry];
  }, [
    formatGramValue,
    hasValidCarbs,
    carbsLabel,
    totalCarbs,
    hasValidProtein,
    proteinLabel,
    totalProtein,
    hasValidFat,
    fatLabel,
    totalFat,
    hasValidCalories,
    caloriesLabel,
    calories,
  ]);
};

// ─── Messages ────────────────────────────────────────────────────────────────

const messages = defineMessages({
  carbs: {
    defaultMessage: 'Carbs',
    description: 'Menu | Nutrition | Carbs | Label',
  },
  protein: {
    defaultMessage: 'Protein',
    description: 'Menu | Nutrition | Protein | Label',
  },
  fat: {
    defaultMessage: 'Fat',
    description: 'Menu | Nutrition | Fat | Label',
  },
  calories: {
    defaultMessage: 'Cal',
    description: 'Menu | Nutrition | Calories | Label',
  },
  gramValue: {
    defaultMessage: '{value}g',
    description: 'Menu | Nutrition | Gram value',
  },
});

// ─── Types ───────────────────────────────────────────────────────────────────

type Params = {
  ingredients: Ingredient[] | readonly Ingredient[];
  calories: number | null;
  useFallbackValues?: boolean;
};

type NutritionData = { label: string; value: string | number }[];

type Ingredient = {
  totalFatG: number | null;
  totalCarbsG: number | null;
  proteinG: number | null;
  quantity?: number;
};
